import { IInsuranceContract, IProposedArticleLine, IStandardCareProposal } from '@alberta/konexi-shared';
import { get } from 'lodash';
import { Maybe } from 'src/app/common/utils/maybe';
import { v4 } from 'uuid';

import { IIntegratedCareProposal } from '../integrated-care/integrated-care-proposal';
import { IMaxAmountOrderedArticleLine } from '../single-order/max-amount-ordered-article-line';
import { IMaxQuantityUpdater } from '../single-order/max-quantity-updater';
import { evaluateCalcMaxQuantity } from './calc-max-quantity-validator';
import { IOrderMaximumQuantity } from './contracts/order-maximum-quantity';
import { calcMaxQuanityBaseOnScope } from './order-functions';
import { OrderedArticleLineViewModel } from './view-models/ordered-article-line-view-model';

export function getMaxQuantityByContractWithOrderLine(
  contract: IInsuranceContract,
  orderedArticleLine: IMaxAmountOrderedArticleLine,
  scope: number
) {
  if (!contract || !orderedArticleLine) {
    return null;
  }

  const contractMaxAmmount = contract.maximumAmountForProductGroups.find(item => {
    return item.productGroupId === orderedArticleLine.associatedArticle.productGroupId;
  });

  if (contractMaxAmmount?.quantity?.quantity != null) {
    return {
      _id: v4(),
      maxQuantity: +contractMaxAmmount.quantity.quantity,
      maxQuantityBaseOnScope: calcMaxQuanityBaseOnScope(+contractMaxAmmount.quantity.quantity, scope),
      currentQuantity: 0,
    };
  }
}

export function getMaxQuantityByStandardCareProposalWithOrderLine(
  careProposal: IIntegratedCareProposal,
  orderedArticleLine: IMaxAmountOrderedArticleLine,
  standardCareProposal: IStandardCareProposal,
  scope: number
): IOrderMaximumQuantity {
  if (!careProposal || !orderedArticleLine || !standardCareProposal || !orderedArticleLine.associatedArticle) {
    return null;
  }

  // Andreas: Was ist wenn der Aritkel aus dem VV enferent wurde.
  // Ich würde hier auf den aufgelöstenen Arikel seine Produktgruppe gehen

  const proposedArticleLine = careProposal.proposedArticleLines.find(
    proposedArticleLine1 => proposedArticleLine1.articleId === orderedArticleLine.articleId
  );
  if (!proposedArticleLine) {
    return null;
  }
  const maxAmountProductGroup = standardCareProposal.sortedProductGroupsWithMaxAmount.find(
    productGroup => productGroup.productGroupId === proposedArticleLine.therapyProductGroupId
  );

  // const maxAmountProductGroup = standardCareProposal.sortedProductGroupsWithMaxAmount.find(maxAmount => {
  //   return maxAmount.productGroupId === orderedArticleLine.associatedArticle.productGroupId;
  // });

  if (maxAmountProductGroup && maxAmountProductGroup.quantity && maxAmountProductGroup.quantity.quantity) {
    return {
      _id: v4(),
      maxQuantity: +maxAmountProductGroup.quantity.quantity,
      maxQuantityBaseOnScope: calcMaxQuanityBaseOnScope(+maxAmountProductGroup.quantity.quantity, scope),
      currentQuantity: 0,
    };
  }
}

export function getMaxQuantityByContractWithProposedLine(
  contract: IInsuranceContract,
  proposedArticleLine: IProposedArticleLine,
  scope: number
): IOrderMaximumQuantity {
  if (!contract || !proposedArticleLine) {
    return null;
  }

  const maxAmountProductGroupQuantity = contract.maximumAmountForProductGroups.find(item => {
    return item.productGroupId === proposedArticleLine.therapyProductGroupId;
  });
  if (
    maxAmountProductGroupQuantity &&
    maxAmountProductGroupQuantity.quantity &&
    maxAmountProductGroupQuantity.quantity.quantity
  ) {
    return {
      _id: v4(),
      maxQuantity: +maxAmountProductGroupQuantity.quantity.quantity,
      maxQuantityBaseOnScope: calcMaxQuanityBaseOnScope(+maxAmountProductGroupQuantity.quantity.quantity, scope),
      currentQuantity: 0,
    };
  }
}

export function getMaxQuantityByStandardCareProposalWithProposedLine(
  standardCareProposal: IStandardCareProposal,
  proposedArticleLine: IProposedArticleLine,
  scope: number
): IOrderMaximumQuantity {
  if (!standardCareProposal || !standardCareProposal.sortedProductGroupsWithMaxAmount || !proposedArticleLine) {
    return null;
  }

  const maxAmountProductGroup = standardCareProposal.sortedProductGroupsWithMaxAmount.find(
    productGroup => productGroup.productGroupId === proposedArticleLine.therapyProductGroupId
  );
  if (maxAmountProductGroup && maxAmountProductGroup.quantity && maxAmountProductGroup.quantity.quantity) {
    return {
      _id: v4(),
      maxQuantity: +maxAmountProductGroup.quantity.quantity,
      maxQuantityBaseOnScope: calcMaxQuanityBaseOnScope(+maxAmountProductGroup.quantity.quantity, scope),
      currentQuantity: 0,
    };
  }
}

export function getMaxQuantityForOrderedArticleLine(
  contract: IInsuranceContract,
  orderedArticleLine: IMaxAmountOrderedArticleLine,
  standardCareProposal: IStandardCareProposal,
  careProposal: IIntegratedCareProposal,
  scope: number
) {
  if (!orderedArticleLine || !orderedArticleLine.associatedArticle) {
    return undefined;
  }
  if (!evaluateCalcMaxQuantity(contract, standardCareProposal, orderedArticleLine.associatedArticle.productGroupId)) {
    return undefined;
  }

  const maxQuantityByContract = getMaxQuantityByContractWithOrderLine(contract, orderedArticleLine, scope);
  const productGroupMaxAmount = getMaxQuantityByStandardCareProposalWithOrderLine(
    careProposal,
    orderedArticleLine,
    standardCareProposal,
    scope
  );

  return new Maybe(maxQuantityByContract).orElse({ maxQuantity: 999999 }).chain(value => get(value, 'maxQuantity')) <=
    new Maybe(productGroupMaxAmount).orElse({ maxQuantity: 999999 }).chain(value => get(value, 'maxQuantity'))
    ? maxQuantityByContract
    : productGroupMaxAmount;
}

export function getOrderMaxQuantityForProposedArticleLine(
  contract: IInsuranceContract,
  proposedArticleLine: IProposedArticleLine,
  standardCareProposal: IStandardCareProposal,
  scope: number
): IOrderMaximumQuantity {
  if (!proposedArticleLine) {
    return undefined;
  }

  if (!evaluateCalcMaxQuantity(contract, standardCareProposal, proposedArticleLine.therapyProductGroupId)) {
    return undefined;
  }

  const contractMaxQuantity = getMaxQuantityByContractWithProposedLine(contract, proposedArticleLine, scope);
  const productGroupMaxAmount = getMaxQuantityByStandardCareProposalWithProposedLine(
    standardCareProposal,
    proposedArticleLine,
    scope
  );

  // The Maybe monad represents computations which might "go wrong" by not returning a value.
  // The Maybe datatype provides a way to make a safety wrapper around partial functions,
  // that is, functions which can fail to work for a range of arguments
  return new Maybe(contractMaxQuantity).orElse({ maxQuantity: 999999 }).chain(value => get(value, 'maxQuantity')) <=
    new Maybe(productGroupMaxAmount).orElse({ maxQuantity: 999999 }).chain(value => get(value, 'maxQuantity'))
    ? contractMaxQuantity
    : productGroupMaxAmount;
}

export function updateMaxQuantityInOrderedArticleLines(
  orderedArticleLines: IMaxAmountOrderedArticleLine[],
  contract: IInsuranceContract,
  maxQuantity: IOrderMaximumQuantity,
  maxQuantityUpdater: IMaxQuantityUpdater
) {
  if (!orderedArticleLines || !orderedArticleLines.length || !this) {
    return undefined;
  }

  for (const articleLine of orderedArticleLines) {
    //  find index of OrderedArticleLine
    if (articleLine) {
      const index = this.orderedArticleLines.findIndex((item: OrderedArticleLineViewModel) => {
        return item.articleId === articleLine.articleId;
      });
      // set contract and Max Quantity
      if (index !== -1) {
        maxQuantityUpdater.setContract.call(this, index, contract);
        if (maxQuantity) {
          // set the Max and add the current Quantity
          this.orderedArticleLines[index].maxAmount = maxQuantity;
          if (articleLine.active) {
            maxQuantityUpdater.setCurrentQuantity.call(this, index);
          }
        }
      }
    }
  }
}
